/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type PDPComparisonResponse,
  type PDPComparisonOptions,
  type PDPComparisonParams,
} from '../interface';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURL}/ecomm/b2c/browse/v2/sku-comparisons`;

const ES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v4/sku-comparisons`;

const getProductComparison = async (
  options: PDPComparisonParams,
  axiosInstance?: AxiosInstance,
) => {
  const {
    parentBrandName,
    subBrandName,
    productId,
    skuId,
    storeNumber,
    country,
    year,
    makeId,
    vehicleTypeId,
    modelId,
    vehicleQuestions,
    vehicleId,
    acesEnabled,
  } = options;

  if (acesEnabled) {
    const response = await getAxios(axiosInstance).get<PDPComparisonResponse>(ES_URL, {
      params: {
        country,
        customerType: 'B2C',
        parentBrandName,
        subBrandName,
        productId,
        skuId,
        storeNumber,
        year,
        makeId,
        modelId,
        vehicleTypeId,
        vehicleQuestions: vehicleQuestions
          ? extractVehicleQuestions(vehicleQuestions, true)
          : undefined,
      },
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    });

    return selector(response.data);
  }

  const response = await getAxios(axiosInstance).get<PDPComparisonResponse>(URL, {
    params: {
      parentBrandName,
      subBrandName,
      productId,
      skuId,
      storeNumber,
      vehicleId,
    },
  });

  return selector(response.data);
};

const selector = (data: PDPComparisonResponse): PDPComparisonResponse => data;

const {
  useData: useProductComparisonData,
  prefetch: prefetchProductComparisonData,
  getFromCache: getProductComparisonFromCacheData,
} = createQuery<PDPComparisonResponse, PDPComparisonParams>(
  'pdpComparison',
  async (options, axiosInstance) => getProductComparison(options, axiosInstance),
);

export const useProductComparison = (options: PDPComparisonOptions & { enabled?: boolean }) => {
  const { data: headerData } = useHeaderData();
  const storeNumber = headerData?.storeNumber;
  const preferredVehicle = getPreferredVehicle(headerData);
  const vehicleId = preferredVehicle?.catalogVehicleId ?? '0';

  return useProductComparisonData({
    ...options,
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: preferredVehicle?.vehicleQuestionsIds,
    storeNumber,
    vehicleId,
    enabled: (options.enabled ?? true) && !!headerData,
  });
};

export const prefetchProductComparison = (
  options: PDPComparisonParams,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetchProductComparisonData(queryClient, options, axiosInstance);
};

export const getProductComparisonFromCache = (
  options: PDPComparisonParams,
  queryClient: QueryClient,
) => {
  return getProductComparisonFromCacheData(queryClient, options);
};
